import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { Option } from '@sapphire/result';

import {
  DrawerBodyComponent,
  DrawerComponent,
  injectDrawerRef,
} from '@ps/core-overlay';
import {
  ButtonComponent,
  IconComponent,
  InfoTooltipIconComponent,
  NumericInputComponent,
  SingleSelectComponent,
} from '@ps/ui';
import { SurveyCreationActionsFacade } from '@ps/survey-creation-data-access';

import {
  TRACKER_CONFIG_DRAWER_TOOLTIPS,
  TRACKER_CONFIG_DRAWER_SUBTITLE,
  TRACKER_CONFIG_DRAWER_TITLE,
  TRACKER_CONFIG_DRAWER_HELP_TEXTS,
} from './utils/tracker-config-drawer.constants';
import {
  TRACKER_CONFIG_DRAWER_EXCLUSION_RULE_OPTIONS,
  TRACKER_CONFIG_DRAWER_FREQUENCY_UNIT_OPTIONS,
} from './utils/tracker-config-drawer-options.constants';
import { trackerConfigDrawerWavesExcludeHelpText } from './utils/tracker-config.helper';

import {
  bindTrackerConfigDrawerFormSchema,
  TRACKER_CONFIG_DRAWER_FORM_DEFAULTS,
} from './form/tracker-config-drawer.schema';
import { toTrackerConfig } from './form/tracker-config-drawer-form.mapper';
import { TrackerConfigFormValues } from './tracker-config-drawer.types';

@Component({
  selector: 'ps-tracker-config-drawer',
  standalone: true,
  imports: [
    DrawerComponent,
    DrawerBodyComponent,
    FormField,
    ButtonComponent,
    IconComponent,
    InfoTooltipIconComponent,
    NumericInputComponent,
    SingleSelectComponent,
  ],
  templateUrl: './tracker-config-drawer.component.html',
  styleUrl: './tracker-config-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackerConfigDrawerComponent {
  private readonly _actions = inject(SurveyCreationActionsFacade);

  private readonly _drawerRef = injectDrawerRef<void>();

  protected readonly title = TRACKER_CONFIG_DRAWER_TITLE;

  protected readonly subtitle = TRACKER_CONFIG_DRAWER_SUBTITLE;

  protected readonly helpTexts = TRACKER_CONFIG_DRAWER_HELP_TEXTS;

  protected readonly tooltips = TRACKER_CONFIG_DRAWER_TOOLTIPS;

  protected readonly frequencyUnitOptions =
    TRACKER_CONFIG_DRAWER_FREQUENCY_UNIT_OPTIONS;

  protected readonly exclusionRuleOptions =
    TRACKER_CONFIG_DRAWER_EXCLUSION_RULE_OPTIONS;

  private readonly _formModel = signal<TrackerConfigFormValues>(
    TRACKER_CONFIG_DRAWER_FORM_DEFAULTS,
  );

  protected readonly trackerConfigForm = form(this._formModel, (path) =>
    bindTrackerConfigDrawerFormSchema(path),
  );

  protected readonly isCustomExclusion = computed(
    () => this._formModel().exclusionRuleKind === 'custom',
  );

  protected readonly selectedExclusionRuleCaption = computed(() =>
    Option.from(
      this.exclusionRuleOptions.find(
        (option) => option.value === this._formModel().exclusionRuleKind,
      ),
    ).mapOr('', (option) => option.caption),
  );

  protected readonly wavesExcludeHelpText = computed(() =>
    trackerConfigDrawerWavesExcludeHelpText(
      Option.from(this._formModel().numberOfWaves).unwrapOr(0),
    ),
  );

  protected onResetToDefault(): void {
    this._formModel.update((current) => ({
      ...TRACKER_CONFIG_DRAWER_FORM_DEFAULTS,
      numberOfWaves: current.numberOfWaves,
    }));
  }

  protected onConfirm(): void {
    if (this.trackerConfigForm().invalid()) {
      this.trackerConfigForm().markAsTouched();
      return;
    }
    this._actions.setupTrackerConfig(toTrackerConfig(this._formModel()));
    this._drawerRef.close();
  }

  protected onCancel(): void {
    this._drawerRef.close();
  }
}
